import { RouterProvider } from "react-router-dom";
import Router from "./constants/Routing";
import { createContext, useEffect, useState, Suspense, useCallback, useRef } from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { analytics, logEvent } from "./constants/firebase-config";
import English from "./lang/en.json";
import Spanish from "./lang/sp.json";
import FontFaceObserver from "fontfaceobserver";
import { QueryClient, QueryClientProvider } from "react-query";
import MedicalRadarLoader from "./components/common/MedicalRadarLoader";
import ErrorBoundary from "./pages/Error/ErrorBoundary";
import { ToastProvider, AppToastContainer } from "./components/common/CustomToast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SubscriptionContext } from "./contexts/SubscriptionContext";
import WithoutSub from "./pages/IndustryProfile/WithoutSub";
import WithSub from "./pages/IndustryProfile/WithSub";
import { SplashApi } from "./constants/API";
import MixpanelManager from "./constants/MixpanelManager";
import mixpanel from 'mixpanel-browser';
import { getUserTargetRoute } from "./utils/authRoutingUtils";
import { syncSplashData } from "./utils/splashAdsUtils";

const queryClient = new QueryClient();

export const Sponsorpost = createContext();
export const Language = createContext();
export const ScrollCordinates = createContext();
export const LoginUserData = createContext();
export const FeedType = createContext();
export const FeedDataRefetch = createContext();
export const PostDetailContext = createContext();
export const NavigationContext = createContext(); // New context for navigation tracking
export const SplashContext = createContext(); // Splash API result context

function App() {
  const id = JSON.parse(localStorage.getItem("SignupData"))?.data?.id || null;

  const [postImage, setPostImage] = useState("");
  const [postDetail, setPostDetail] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [lang, setLang] = useState(() => {
    try {
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);
        const urlLang = urlParams.get("lang");
        if (urlLang) {
          const lower = urlLang.toLowerCase().trim();
          if (lower === "es" || lower === "spanish" || lower === "sp") {
            localStorage.setItem("selectedLanguage", "Spanish");
            return Spanish;
          }
          if (lower === "en" || lower === "english") {
            localStorage.setItem("selectedLanguage", "English");
            return English;
          }
        }
        if (urlParams.has("es") || urlParams.has("spanish")) {
          localStorage.setItem("selectedLanguage", "Spanish");
          return Spanish;
        }
        if (window.location.pathname.toLowerCase().includes("/es")) {
          localStorage.setItem("selectedLanguage", "Spanish");
          return Spanish;
        }
      }
    } catch (e) {}
    const savedLang = localStorage.getItem("selectedLanguage");
    return savedLang === "Spanish" ? Spanish : English;
  });
  const [refetch, setRefetch] = useState(false);
  const [isHomeRefreshing, setIsHomeRefreshing] = useState(false);
  const [homeRefreshVersion, setHomeRefreshVersion] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [splashData, setSplashData] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("SplashData"));
      if (saved && saved.user) return saved;
      const signupSaved = JSON.parse(localStorage.getItem("SignupData"));
      if (signupSaved?.data) {
        return {
          user: {
            id: signupSaved.data.id || id,
            name: signupSaved.data.name,
            image: signupSaved.data.image,
            user_name: signupSaved.data.user_name,
            user_type: signupSaved.data.user_type,
            ...signupSaved.data,
          },
        };
      }
      return { user: { id: id } };
    } catch {
      return { user: { id: id } };
    }
  });
  const [feedType, setFeedType] = useState(
    () => sessionStorage.getItem("FeedType") || "all"
  );
  const [isAppReady, setIsAppReady] = useState(false);
  const [showLoader, setShowLoader] = useState(true); // Start with loader
  const [splashResult, setSplashResult] = useState(null); // Splash API result
  
  const [showNoSubModal, setShowNoSubModal] = useState(false);
  const [showWithSubModal, setShowWithSubModal] = useState(false);

  // New states for navigation tracking
  const [currentUrl, setCurrentUrl] = useState(window.location.pathname);
  const [previousUrl, setPreviousUrl] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);
  
  // Subscription states — initialize from localStorage on app load
  const [activeSubscription, setActiveSubscription] = useState(() => {
    try {
      const splashSaved = JSON.parse(localStorage.getItem("SplashData"));
      const signupSaved = JSON.parse(localStorage.getItem("SignupData"));
      return splashSaved?.active_subscription || signupSaved?.active_subscription || null;
    } catch { return null; }
  });
  const [benefits, setBenefits] = useState(() => {
    try {
      const splashSaved = JSON.parse(localStorage.getItem("SplashData"));
      const signupSaved = JSON.parse(localStorage.getItem("SignupData"));
      return splashSaved?.benefits || signupSaved?.benefits || [];
    } catch { return []; }
  });
  
  const MIN_LOADER_DURATION = import.meta.env.PROD ? 2000 : 1500;

  // Language update function
  const updateLang = (newLanguage) => {
    if (newLanguage === "English") {
      setLang(English);
      localStorage.setItem("selectedLanguage", "English");
    } else if (newLanguage === "Spanish") {
      setLang(Spanish);
      localStorage.setItem("selectedLanguage", "Spanish");
    }
  };

  // Context update functions
  const updateScrollPosition = (newValue) => setScrollPosition(newValue);
  const updateContext = (newValue) => setPostImage(newValue);
  const updateSplash = (newValue) => setSplashData(newValue);
  const syncSplashResult = (newJson) => {
    return syncSplashData(newJson, splashResult, (val) => {
      setSplashResult(val);
      setSplashData(val);
    });
  };
  const updateFeedType = (newValue) => {
    setFeedType(newValue);
    sessionStorage.setItem("FeedType", newValue);
  };
  const homeRefreshTimeoutRef = useRef(null);

  const finishHomeRefresh = useCallback(() => {
    if (homeRefreshTimeoutRef.current) {
      clearTimeout(homeRefreshTimeoutRef.current);
      homeRefreshTimeoutRef.current = null;
    }
    setIsHomeRefreshing(false);
  }, []);

  const refreshHomeFeed = useCallback(() => {
    if (homeRefreshTimeoutRef.current) {
      clearTimeout(homeRefreshTimeoutRef.current);
    }
    setIsHomeRefreshing(true);
    setHomeRefreshVersion((version) => version + 1);
    // Safety fallback auto-dismiss after 4s so the loader is never infinite
    homeRefreshTimeoutRef.current = setTimeout(() => {
      setIsHomeRefreshing(false);
      homeRefreshTimeoutRef.current = null;
    }, 4000);
  }, []);

  const updateRefetch = (newValue) => setRefetch(newValue);
  const updatePostDetail = (newValue) => {
    setPostDetail(newValue);
    setIsOpen(true);
  };
  const postModalClose = () => {
    setPostDetail({});
    setIsOpen(false);
  };

  // Subscription context functions
  const updateSubscription = (subscription) => setActiveSubscription(subscription);
  const updateBenefits = (newBenefits) => setBenefits(newBenefits);
  const clearSubscription = () => {
    setActiveSubscription(null);
    setBenefits([]);
  };

  // Navigation tracking functions
  const updateCurrentUrl = (newUrl) => {
    
    // Don't update if it's the same URL
    if (newUrl === currentUrl) return;
    
    // Set previous URL
    setPreviousUrl(currentUrl);
    
    // Update current URL
    setCurrentUrl(newUrl);
    
    // Update navigation history (keep last 10 entries)
    setNavigationHistory(prev => {
      const newHistory = [...prev, { url: newUrl, timestamp: Date.now() }];
      return newHistory.slice(-10); // Keep only last 10 entries
    });
    
    // Store in sessionStorage for persistence across refreshes
    sessionStorage.setItem("currentUrl", newUrl);
    if (currentUrl) {
      sessionStorage.setItem("previousUrl", currentUrl);
    }
  };

  const getReturnUrl = () => {
    // Get the URL to return to after completing profile
    // Exclude auth-related pages
    const excludedPaths = ['/login', '/signup', '/register', '/auth'];
    
    // Check previous URL first
    if (previousUrl && !excludedPaths.some(path => previousUrl.toLowerCase().includes(path.toLowerCase()))) {
      return previousUrl;
    }
    
    // Check navigation history for a valid return URL
    const validHistory = navigationHistory
      .filter(entry => !excludedPaths.some(path => entry.url.toLowerCase().includes(path.toLowerCase())))
      .reverse(); // Get most recent first
    
    if (validHistory.length > 0) {
      return validHistory[0].url;
    }
    
    // Default fallback
    return "/feed";
  };

  // Handle browser back/forward — show loader immediately, run splash checks, then navigate to correct route
  useEffect(() => {
    let debounceTimer = null;
    let isHandling = false;

    const handlePopState = () => {
      // Debounce: wait for rapid clicks to settle, then run one splash check silently in background
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        if (isHandling) return;
        isHandling = true;

        const token = (() => {
          try { return JSON.parse(localStorage.getItem("SignupData"))?.data?.token; }
          catch { return null; }
        })();

        if (!token) { isHandling = false; return; }

        try {
          const json = await fetch(SplashApi, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json());

          if (json?.status === false && json?.action === "Unauthenticated request") {
            localStorage.clear();
            sessionStorage.clear();
            window.location.replace("/");
            return;
          }

          if (json?.status && json?.user) {
            syncSplashResult(json);
            if (json.active_subscription) setActiveSubscription(json.active_subscription);
            if (Array.isArray(json.benefits)) setBenefits(json.benefits);

            // Identify user in Mixpanel with splash data
            const userData = json.user;
            if (userData?.id) {
              MixpanelManager.identify(userData.id, userData.email, userData.name, userData.profile_image);
              if (mixpanel && typeof mixpanel.people.set === 'function') {
                mixpanel.people.set({
                  'hcp_score': userData.score || '0',
                  'user_type': userData.user_type,
                  'verified': userData.verified,
                });
              }
            }
            syncMentorApprovalFlag(userData);

            const currentPath = window.location.pathname;
            const target = getUserTargetRoute(json.user, currentPath);
            if (target && target.toLowerCase() !== currentPath.toLowerCase()) {
              Router.navigate(target, { replace: true });
            }
          }
        } catch {}
        isHandling = false;
      }, 300);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, []);

  // Listen for no-subscription modal event
  useEffect(() => {
    const handler = () => setShowNoSubModal(true);
    const handlerWithSub = () => setShowWithSubModal(true);
    window.addEventListener('show-no-subscription-modal', handler);
    window.addEventListener('show-with-subscription-modal', handlerWithSub);
    return () => {
      window.removeEventListener('show-no-subscription-modal', handler);
      window.removeEventListener('show-with-subscription-modal', handlerWithSub);
    };
  }, []);

  // Track URL changes
  useEffect(() => {
    const handleLocationChange = () => {
      const newUrl = window.location.pathname;
      updateCurrentUrl(newUrl);
    };

    // Listen for popstate events (back/forward button)
    window.addEventListener('popstate', handleLocationChange);
    
    // Check for URL changes periodically (for programmatic navigation)
    const intervalId = setInterval(() => {
      const newUrl = window.location.pathname;
      if (newUrl !== currentUrl) {
        updateCurrentUrl(newUrl);
      }
    }, 100);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      clearInterval(intervalId);
    };
  }, [currentUrl]);

  // Initialize from sessionStorage on app load
  useEffect(() => {
    const storedCurrentUrl = sessionStorage.getItem("currentUrl");
    const storedPreviousUrl = sessionStorage.getItem("previousUrl");
    
    if (storedCurrentUrl && storedCurrentUrl !== currentUrl) {
      setCurrentUrl(storedCurrentUrl);
    }
    
    if (storedPreviousUrl) {
      setPreviousUrl(storedPreviousUrl);
    }
  }, []);

  // Ensure SplashData always has latest auth subscription payload from SignupData.
  // This keeps permission checks stable immediately after login/refresh.
  useEffect(() => {
    try {
      const splashSaved = JSON.parse(localStorage.getItem("SplashData") || "{}");
      const signupSaved = JSON.parse(localStorage.getItem("SignupData") || "{}");
      const signupUser = signupSaved?.data || null;
      const signupBenefits = Array.isArray(signupSaved?.benefits) ? signupSaved.benefits : null;
      const signupActiveSubscription = signupSaved?.active_subscription ?? null;

      if (!signupUser) return;

      const needsUserSync = !splashSaved?.user?.id;
      const needsBenefitSync =
        (!Array.isArray(splashSaved?.benefits) || splashSaved.benefits.length === 0) &&
        Array.isArray(signupBenefits);
      const needsSubscriptionSync =
        !splashSaved?.active_subscription && signupActiveSubscription;

      if (needsUserSync || needsBenefitSync || needsSubscriptionSync) {
        const nextSplash = {
          ...splashSaved,
          user: splashSaved?.user || signupUser,
          active_subscription: splashSaved?.active_subscription || signupActiveSubscription || null,
          benefits: Array.isArray(splashSaved?.benefits) && splashSaved.benefits.length > 0
            ? splashSaved.benefits
            : signupBenefits || [],
        };
        localStorage.setItem("SplashData", JSON.stringify(nextSplash));
        setSplashData(nextSplash);
      }
    } catch (error) {
      console.error("Failed to hydrate SplashData from SignupData:", error);
    }
  }, []);

  // Consolidated font loading, splash API call, and minimum loader time
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load fonts
        const fonts = ["Onest"];
        const fontObservers = fonts.map((family) => new FontFaceObserver(family));

        // Run fonts + splash API in parallel
        const token = (() => {
          try { return JSON.parse(localStorage.getItem("SignupData"))?.data?.token; }
          catch { return null; }
        })();

        const splashPromise = token
          ? fetch(SplashApi, { headers: { Authorization: `Bearer ${token}` } })
              .then((r) => r.json())
             .then((json) => {
  
  if (json?.status === false && json?.action === "Unauthenticated request") {
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace("/");
    return;
  }

  if (json?.status && json?.user) {
                  setSplashResult(json);
                  setSplashData(json);
                  // Sync subscription data
                  try {
                    const existing = JSON.parse(localStorage.getItem("SplashData") || "{}");
                    localStorage.setItem("SplashData", JSON.stringify({ ...existing, ...json }));
                  } catch {}
                  if (json.active_subscription) setActiveSubscription(json.active_subscription);
                  if (Array.isArray(json.benefits)) setBenefits(json.benefits);

                  // Identify user in Mixpanel with splash data
                  const userData = json.user;
                  if (userData?.id) {
                    MixpanelManager.identify(userData.id, userData.email, userData.name, userData.profile_image);
                    if (mixpanel && typeof mixpanel.people.set === 'function') {
                      mixpanel.people.set({
                        'hcp_score': userData.score || '0',
                        'user_type': userData.user_type,
                        'verified': userData.verified,
                      });
                    }
                  }
                  syncMentorApprovalFlag(userData);

                  // ── Routing decision during loader ──
                  const currentPath = window.location.pathname;
                  const target = getUserTargetRoute(json.user, currentPath);
                  if (target && target.toLowerCase() !== currentPath.toLowerCase()) {
                    Router.navigate(target, { replace: true });
                  }
                }
              })
              .catch(() => {})
          : Promise.resolve();

        await Promise.all([
          Promise.all(fontObservers.map((observer) => observer.load(null, 5000))).catch(() => {}),
          splashPromise,
          new Promise((resolve) => setTimeout(resolve, MIN_LOADER_DURATION)),
        ]);

        setIsAppReady(true);
        setShowLoader(false);
      } catch (error) {
        console.error("App initialization failed:", error);
        setIsAppReady(true);
        setShowLoader(false);
      }
    };

    initializeApp();
  }, []);

  // Prefetch primary tab chunks in the background once the app is idle
  // so that switching between tabs is instantaneous without any delay or chunk loader
  useEffect(() => {
    const preload = () => {
      import("./pages/Feed");
      import("./pages/agenda/Agenda");
      import("./pages/course/Courses");
      import("./components/Mentorship/Mentorship");
      import("./pages/Events");
      import("./pages/notifications/Index");
      import("./pages/chat/ChatModule");
      import("./pages/search/Search");
      import("./pages/Profile");
    };
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(preload, { timeout: 3000 })
      : window.setTimeout(preload, 1000);
    return () => {
      if (window.requestIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, []);

  // Initial setup (timezone, language detection, body class)
  useEffect(() => {
    document.body.classList.add("scroll-hide");
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    sessionStorage.setItem("user_timezone", userTimezone);

    // Check if language is already saved in localStorage
    const savedLanguage = localStorage.getItem("selectedLanguage");
    
    if (savedLanguage) {
      // Use saved language
      if (savedLanguage === "Spanish") {
        setLang(Spanish);
      } else {
        setLang(English);
      }
    } else {
      // Only detect browser language if no saved preference
      const browserLang = navigator.language.toLowerCase();

      if (browserLang.startsWith("en")) {
        setLang(English);
        localStorage.setItem("selectedLanguage", "English");
      } else if (browserLang.startsWith("es")) {
        setLang(Spanish);
        localStorage.setItem("selectedLanguage", "Spanish");
      } else {
        // Default to English if not en or es
        setLang(English);
        localStorage.setItem("selectedLanguage", "English");
      }
    }
  }, []);

  // Firebase analytics
  useEffect(() => {
    const startTime = performance.now();

    window.addEventListener("load", () => {
      const endTime = performance.now();
      const pageLoadTime = endTime - startTime;
      logEvent(analytics, "page_load_time", { load_time: pageLoadTime });
    });

    logEvent(analytics, "app_loaded", { page_path: window.location.pathname });
  }, []);
return (
    <div>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <NavigationContext.Provider value={{ 
            currentUrl, 
            previousUrl, 
            navigationHistory, 
            updateCurrentUrl, 
            getReturnUrl 
          }}>
            <SubscriptionContext.Provider value={{ 
              activeSubscription, 
              benefits, 
              updateSubscription, 
              updateBenefits, 
              clearSubscription 
            }}>
              <SplashContext.Provider value={{ splashResult, setSplashResult, syncSplashResult }}>
                <LoginUserData.Provider value={{ splashData, updateSplash, lang, updateLang }}>
                  <Language.Provider value={{ lang, updateLang }}>
                    <ToastProvider>
                      <ScrollCordinates.Provider value={{ scrollPosition, updateScrollPosition }}>
                        <Sponsorpost.Provider value={{ postImage, updateContext }}>
                          <FeedType.Provider value={{ feedType, updateFeedType }}>
                            <FeedDataRefetch.Provider value={{
                              refetch,
                              updateRefetch,
                              isHomeRefreshing,
                              homeRefreshVersion,
                              refreshHomeFeed,
                              finishHomeRefresh,
                            }}>
                              <PostDetailContext.Provider
                                value={{ postDetail, isOpen, postModalClose, updatePostDetail }}
                              >
                                <AppToastContainer />
                                <ToastContainer style={{ zIndex: 100005 }} />
                                <WithoutSub isOpen={showNoSubModal} onClose={() => setShowNoSubModal(false)} />
                                <WithSub isOpen={showWithSubModal} onClose={() => setShowWithSubModal(false)} />
                                {showLoader ? (
                                  <div style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: '#fff' }}>
                                    <MedicalRadarLoader />
                                  </div>
                                ) : (
                                  <Suspense fallback={<div style={{ minHeight: '100vh', background: '#F4F7FB' }} />}>
                                    <RouterProvider router={Router} />
                                  </Suspense>
                                )}
                              </PostDetailContext.Provider>
                            </FeedDataRefetch.Provider>
                          </FeedType.Provider>
                        </Sponsorpost.Provider>
                      </ScrollCordinates.Provider>
                    </ToastProvider>
                  </Language.Provider>
                </LoginUserData.Provider>
              </SplashContext.Provider>
            </SubscriptionContext.Provider>
          </NavigationContext.Provider>
        </ErrorBoundary>
      </QueryClientProvider>
    </div>
  );
}

export default App;
